import NetworkManager from './NetworkManager';

// Autocomplete results come back from Byte Graph in relevance order, which
// users perceive as "random." Sort alphabetically by name (case-insensitive,
// locale-aware) before handing to the UI.
const sortByName = <T extends { name: string }>(items: T[]): T[] =>
  [...items].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));

export interface ProductSearchResult {
  code: string;
  name: string;
  description?: string;
  label: string;
}

export interface BundleSearchResult {
  code: string;
  name: string;
  label: string;
}

export interface VariantSearchResult {
  code: string;
  name: string;
  label: string;
}

export interface ModifierSearchResult {
  code: string;
  name: string;
  label: string;
}

export interface OptionValueSearchResult {
  code: string;
  name: string;
  label: string;
}

/**
 * Search for products by query string
 */
export async function searchProducts(query: string): Promise<ProductSearchResult[]> {
  if (!query || query.length < 2) {
    return [];
  }

  try {
    const response = await NetworkManager.get<{ status: boolean; data: ProductSearchResult[] }>(
      `/products/search?query=${encodeURIComponent(query)}`
    );
    return sortByName(response.data || []);
  } catch (error) {
    console.error('Failed to search products:', error);
    // Log more details for debugging
    if (error instanceof Error) {
      console.error('Error details:', {
        message: error.message,
        response: (error as any).response?.data,
        status: (error as any).response?.status,
      });
    }
    return [];
  }
}

/**
 * Search for bundles by query string
 */
export async function searchBundles(query: string): Promise<BundleSearchResult[]> {
  if (!query || query.length < 2) {
    return [];
  }

  try {
    const response = await NetworkManager.get<{ status: boolean; data: BundleSearchResult[] }>(
      `/bundles/search?query=${encodeURIComponent(query)}`
    );
    return sortByName(response.data || []);
  } catch (error) {
    console.error('Failed to search bundles:', error);
    return [];
  }
}

/**
 * Search for variants by query string
 */
export async function searchVariants(query: string): Promise<VariantSearchResult[]> {
  if (!query || query.length < 2) {
    return [];
  }

  try {
    const response = await NetworkManager.get<{ status: boolean; data: VariantSearchResult[] }>(
      `/variants/search?query=${encodeURIComponent(query)}`
    );
    return response.data || [];
  } catch (error) {
    console.error('Failed to search variants:', error);
    return [];
  }
}

/**
 * Fetch the variants of a single product from the catalog. Used by the
 * Snooze Menu Item dialog after a product is selected, to power the
 * optional "Specific variants only" picker. Returns an empty list when
 * the product has no variants or doesn't exist — callers should hide the
 * variant picker in that case.
 */
export async function getProductVariants(productCode: string): Promise<VariantSearchResult[]> {
  if (!productCode) {
    return [];
  }
  try {
    const response = await NetworkManager.get<{ status: boolean; data: VariantSearchResult[] }>(
      `/products/${encodeURIComponent(productCode)}/variants`
    );
    return response.data || [];
  } catch (error) {
    // 404 means "no such product in the catalog" — degrade gracefully so the
    // dialog falls back to product-only snooze rather than blocking the user.
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status !== 404) {
      console.error('Failed to fetch product variants:', error);
    }
    return [];
  }
}

/**
 * Search for modifiers by query string
 */
export async function searchModifiers(query: string): Promise<ModifierSearchResult[]> {
  if (!query || query.length < 2) {
    return [];
  }

  try {
    const response = await NetworkManager.get<{ status: boolean; data: ModifierSearchResult[] }>(
      `/modifiers/search?query=${encodeURIComponent(query)}`
    );
    return sortByName(response.data || []);
  } catch (error) {
    console.error('Failed to search modifiers:', error);
    return [];
  }
}

/**
 * Search for option values by query string
 */
export async function searchOptionValues(query: string): Promise<OptionValueSearchResult[]> {
  if (!query || query.length < 2) {
    return [];
  }

  try {
    const response = await NetworkManager.get<{ status: boolean; data: OptionValueSearchResult[] }>(
      `/option-values/search?query=${encodeURIComponent(query)}`
    );
    return sortByName(response.data || []);
  } catch (error) {
    console.error('Failed to search option values:', error);
    return [];
  }
}
